import { useEffect, useState } from "react";
import { adminAnalytics, formatApiError } from "@/lib/api";
import StatCard from "../components/StatCard";
import AdminCard from "../components/AdminCard";

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    adminAnalytics()
      .then((d) => { if (mounted) setData(d); })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;
  if (err) return <p className="text-red-600 text-sm">{err}</p>;
  if (!data) return null;

  const maxDaily = Math.max(1, ...data.generations_per_day.map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total users"  value={data.total_users}       testId="stat-users" />
        <StatCard label="Admins"       value={data.total_admins}      testId="stat-admins" />
        <StatCard label="Generations"  value={data.total_generations} testId="stat-generations" />
        <StatCard label="AI Images"    value={data.total_images}      testId="stat-images" />
      </div>

      {/* Generations chart */}
      <AdminCard>
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-5">
            Generations · last 7 days
          </p>
          <div className="flex items-end gap-3 h-48" data-testid="generations-chart">
            {data.generations_per_day.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end">
                <div
                  className="w-full bg-indigo-500 rounded-t-sm transition-all hover:bg-indigo-600"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? "4px" : "0" }}
                  title={`${d.date}: ${d.count}`}
                />
                <div className="text-[9px] font-medium text-slate-400 mt-2 uppercase">{d.date.slice(5)}</div>
                <div className="text-[10px] font-bold text-slate-700">{d.count}</div>
              </div>
            ))}
          </div>
        </div>
      </AdminCard>

      {/* Top creators */}
      {data.top_users.length > 0 && (
        <AdminCard header="Top creators">
          <div>
            {data.top_users.map((t, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-6 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                data-testid={`top-user-${i}`}
              >
                <div>
                  <div className="font-semibold text-slate-900">{t.user.name || t.user.email}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.user.email}</div>
                </div>
                <div className="text-2xl font-bold text-indigo-600">{t.generations}</div>
              </div>
            ))}
          </div>
        </AdminCard>
      )}
    </div>
  );
}
